import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { getApiBaseUrl } from "../../../CommonMethods/CommonMethods";
import { projectHistoryData, projectDetails } from "./projectHistoryData";

// ============================================================================
// PROJECT HISTORY APIs - All APIs for Project History
// ============================================================================

/**
 * Fetch Project Details for Ribbon Section
 * 
 * API Type: GET
 * API Endpoint: GET /{appName}/{network}/{collabType}/{roleName}/projectdetails
 * 
 * Example:
 *   GET /projectdetails?projectId=AR%20-%20LDD
 * 
 * Request Payload:
 * {
 *   "projectId": "AR - LDD"
 * }
 * 
 * Response Structure:
 * {
 *   "status": true,
 *   "message": "Project details fetched successfully",
 *   "data": {
 *     "projectId": "AR - LDD",
 *     "poNumber": "GT50345",
 *     "mbrNumber": "MBR-001",
 *     "productDescription": "Actazosin Step 3",
 *     "cmoSite": "Sai - HYD",
 *     "batchNumbers": "400682, 400683, 400684",
 *     "deliveryNumber": "DEL-001"
 *   }
 * }
 */
export const fetchProjectDetailsAPI = async (projectId) => {
  try {
    // Console log for GET API call (when API is ready, this will show real call)
    console.log('========================================');
    console.log('📡 GET API: projectdetails');
    console.log('API Endpoint: GET /{appName}/{network}/{collabType}/{roleName}/projectdetails');
    console.log('Query Params:', JSON.stringify({ projectId }, null, 2));
    console.log('========================================');

    // COMMENTED OUT - Real API call (uncomment when API is ready)
    // const apiBaseUrl = getApiBaseUrl(appUrl);
    // if (apiBaseUrl) {
    //   const response = await axios.get(
    //     `${apiBaseUrl}/projectdetails`,
    //     { params: { projectId } }
    //   );
    //   
    //   if (response?.data?.status && response?.data?.data) {
    //     console.log('Response:', response.data);
    //     console.log('========================================');
    //     return response.data;
    //   }
    //   throw new Error(response?.data?.message || 'Failed to fetch project details');
    // }

    // Static response for now (API is commented) - matches actual API structure
    const staticResponse = {
      status: true,
      message: "Project details fetched successfully",
      data: projectDetails || {
        projectId: projectId || "AR - LDD",
        poNumber: "GT50345",
        mbrNumber: "MBR-001",
        productDescription: "Actazosin Step 3",
        cmoSite: "Sai - HYD",
        batchNumbers: "400682, 400683, 400684",
        deliveryNumber: "DEL-001"
      }
    };

    console.log('Response:', JSON.stringify(staticResponse, null, 2));
    console.log('========================================');

    return staticResponse;
  } catch (error) {
    console.error('Error fetching project details:', error);
    throw error;
  }
};

/**
 * Fetch Project History with Search Filters
 * 
 * API Type: POST
 * API Endpoint: POST /{appName}/{network}/{collabType}/{roleName}/projecthistory
 * 
 * Request Payload Examples:
 * 
 * 1. No filter (show all):
 * {
 *   "projectId": "AR - LDD",
 *   "searchCriteria": "all",
 *   "pageNumber": 1,
 *   "pageSize": 10
 * }
 * 
 * 2. Event search:
 * {
 *   "projectId": "AR - LDD",
 *   "searchCriteria": "event",
 *   "searchValue": "Confirmed",
 *   "pageNumber": 1,
 *   "pageSize": 10
 * }
 * 
 * 3. Month search:
 * {
 *   "projectId": "AR - LDD",
 *   "searchCriteria": "month",
 *   "searchValue": "January",
 *   "pageNumber": 1,
 *   "pageSize": 10
 * }
 * 
 * Response Structure:
 * {
 *   "status": true,
 *   "message": "Project history fetched successfully",
 *   "data": {
 *     "projectHistory": [
 *       {
 *         "id": 1,
 *         "dateTime": "30-08-2025 18:16:00",
 *         "eventDescription": "Project is Confirmed, Campaign TYR12345 is Completed",
 *         "projectStatus": "Confirmed",
 *         "campaignStatus": "Completed",
 *         "batchStatus": "Ready for Dispatch",
 *         "organization": "Sai Life Sciences",
 *         "role": "Planner",
 *         "username": "eddie.wyatt@sailifesciences.com"
 *       }
 *     ],
 *     "totalRecords": 50,
 *     "currentPage": 1,
 *     "totalPages": 5
 *   }
 * }
 * 
 * Note: Response does NOT include pageNumber and pageSize (only totalRecords, currentPage, totalPages)
 */
export const fetchProjectHistoryAPI = async (params = {}) => {
  try {
    const { projectId, searchCriteria, searchValue, pageNumber, pageSize } = params;

    // Validate required parameters
    if (!projectId) {
      throw new Error('projectId is required');
    }

    if (!searchCriteria || !['all', 'event', 'month'].includes(searchCriteria)) {
      throw new Error('searchCriteria must be "all", "event", or "month"');
    }

    // Prepare payload based on searchCriteria
    const payload = {
      projectId: projectId,
      searchCriteria: searchCriteria,
      pageNumber: pageNumber || 1,
      pageSize: pageSize || 10,
    };

    if ((searchCriteria === 'event' || searchCriteria === 'month') && searchValue) {
      payload.searchValue = searchValue;
    }

    // Console log for POST API call (when API is ready, this will show real call)
    console.log('========================================');
    console.log('📡 POST API: projecthistory');
    console.log('API Endpoint: POST /{appName}/{network}/{collabType}/{roleName}/projecthistory');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    console.log('========================================');

    // COMMENTED OUT - Real API call (uncomment when API is ready)
    // const apiBaseUrl = getApiBaseUrl(appUrl);
    // if (apiBaseUrl) {
    //   const response = await axios.post(
    //     `${apiBaseUrl}/projecthistory`,
    //     payload
    //   );
    //   
    //   if (response?.data?.status && response?.data?.data) {
    //     console.log('Response:', response.data);
    //     console.log('========================================');
    //     return response.data;
    //   }
    //   throw new Error(response?.data?.message || 'Failed to fetch project history');
    // }

    // Static response for now (API is commented) - matches actual API structure
    // Filter data based on searchCriteria for demo purposes
    let filteredData = projectHistoryData || [];
    
    if (searchCriteria === 'event' && searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = projectHistoryData.filter(item => 
        item.eventDescription?.toLowerCase().includes(searchLower) ||
        item.projectStatus?.toLowerCase().includes(searchLower) ||
        item.campaignStatus?.toLowerCase().includes(searchLower) ||
        item.batchStatus?.toLowerCase().includes(searchLower)
      );
    } else if (searchCriteria === 'month' && searchValue) {
      const monthMap = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4,
        'May': 5, 'June': 6, 'July': 7, 'August': 8,
        'September': 9, 'October': 10, 'November': 11, 'December': 12
      };
      const monthNumber = monthMap[searchValue];
      
      if (monthNumber) {
        filteredData = projectHistoryData.filter(item => {
          if (!item.dateTime) return false;
          const date = new Date(item.dateTime);
          const month = date.getMonth() + 1; // getMonth() returns 0-11
          return month === monthNumber;
        });
      } else {
        filteredData = [];
      }
    }

    // Apply pagination
    const totalRecords = filteredData.length;
    const totalPages = Math.ceil(totalRecords / (pageSize || 10));
    const startIndex = ((pageNumber || 1) - 1) * (pageSize || 10);
    const endIndex = startIndex + (pageSize || 10);
    const paginatedData = filteredData.slice(startIndex, endIndex);

    const staticResponse = {
      status: true,
      message: totalRecords === 0 
        ? "No project history found matching your search criteria"
        : "Project history fetched successfully",
      data: {
        projectHistory: paginatedData,
        totalRecords: totalRecords,
        currentPage: pageNumber || 1,
        totalPages: totalPages
        // Note: pageNumber and pageSize are NOT included in response
      }
    };

    console.log('Response:', JSON.stringify(staticResponse, null, 2));
    console.log(`Total Records: ${totalRecords}, Current Page: ${pageNumber || 1}, Total Pages: ${totalPages}`);
    console.log('========================================');

    return staticResponse;
  } catch (error) {
    console.error('Error fetching project history:', error);
    throw error;
  }
};

